import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLazyQuery, useReactiveVar } from '@apollo/client'
import { debounce } from 'lodash'
import ReactPixel from 'react-facebook-pixel'
import { useTranslation } from 'react-i18next'

import { breakpoints } from '@src/constants/breakpoints'
import { screenResolutionVar } from '@src/models/screenResolution'
import { MenuItemTextSearchDocument } from '@src/pages/OutletPage/OutletMenu/queries/__generated__/MenuItemTextSearch.graphql-interface'

import { ExpandButton } from '../../ServiceNavigation/MobileNavigationStyles'
import { SearchHead } from '../SearchHead'
import { SearchHeadMobile } from '../SearchHeadMobile'
import { MobileOutletMenuSearchContainer } from '../SearchHeadStyles'
import { useOutletPageContext } from '@src/pages/OutletPage/OutletPageContext'

export const OutletMenuSearch: React.FC<{
  outletId: string
  showSearchBar: boolean
  setShowSearchBar: (val: boolean) => void
}> = ({ outletId, showSearchBar, setShowSearchBar }) => {
  const {
    isStackedMenu,
    searchTerm,
    setSearchTerm,
    showMenuSearchResults,
    setShowMenuSearchResults,
    setMenuSearchIds,
  } = useOutletPageContext()

  const { width } = useReactiveVar(screenResolutionVar)
  const { t } = useTranslation('serviceNavigation')
  const [sendSearchQuery, { data }] = useLazyQuery(MenuItemTextSearchDocument)
  const [startAnimation, setStartAnimation] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const executeSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        if (searchTerm.length >= 3) {
          setShowMenuSearchResults(true)
          window.scrollTo({ top: 0 })
          void sendSearchQuery({
            variables: {
              outletId: outletId,
              searchQuery: searchTerm,
            },
          })
          ReactPixel.track('Search', {
            search_string: searchTerm,
            content_type: 'product',
          })
        } else {
          setMenuSearchIds([])
          setShowMenuSearchResults(false)
        }
      }, 500),
    [outletId, sendSearchQuery, setMenuSearchIds, setShowMenuSearchResults]
  )

  useEffect(() => {
    if (data?.menuItemTextSearch.length) {
      setMenuSearchIds(
        data?.menuItemTextSearch.map(({ menuItemId }) => menuItemId)
      )
    } else setMenuSearchIds([])
  }, [data?.menuItemTextSearch, setMenuSearchIds])

  const handleClose = () => {
    if (startClosingAnimation) {
      startClosingAnimation()
    }
  }

  useEffect(() => {
    if (width >= breakpoints.tablet) {
      if (!isStackedMenu) setShowSearchBar(false)
      setStartAnimation(false)
    }
  }, [width, isStackedMenu, setShowSearchBar])

  const startClosingAnimation = () => {
    setStartAnimation(true)
    setShowSearchBar(false)
    setTimeout(() => {
      setStartAnimation(false)
    }, 1000)
  }

  const searchBarPlaceholder = () => {
    if (showSearchBar || isStackedMenu) return t('search_for_item')
    return ''
  }

  return (
    <>
      {width > breakpoints.tabletMisc ? (
        <SearchHead
          executeSearch={executeSearch}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          closeSearchResults={() => setShowMenuSearchResults(false)}
          showMenuSearchResults={showMenuSearchResults}
          isMenuView
        />
      ) : (
        <MobileOutletMenuSearchContainer>
          <SearchHeadMobile
            executeSearch={executeSearch}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            closeSearchResults={() => setShowMenuSearchResults(false)}
            showSearchBar={showSearchBar}
            startAnimation={startAnimation}
            startClosingAnimation={handleClose}
            placeholder={searchBarPlaceholder()}
            isStackedMenu={isStackedMenu}
            isMenuView
            inputRef={searchInputRef}
          />

          <ExpandButton
            onClick={() => {
              if (!showSearchBar) {
                setShowSearchBar(true)
                setTimeout(() => {
                  searchInputRef.current?.focus()
                }, 300)
              }
            }}
            $display={!showSearchBar}
            $isMenuView
          />
        </MobileOutletMenuSearchContainer>
      )}
    </>
  )
}
